/**
 * @fileoverview EditCategoryList - Category list component for inline transaction editing.
 *
 * Renders category selection dropdowns and amount inputs for editing transactions
 * in inline edit mode (used in TransactionList). Supports transaction-type-specific
 * category filtering, grandfathering of historical categories, and dynamic row management.
 *
 * @description This component provides:
 * - Transaction-type-specific category filtering (Expense, Transfer, Income, Adjustment)
 * - Grandfathering support for categories that were valid when transaction was created
 * - Grey italic styling for grandfathered categories
 * - Dynamic add/remove category rows (except for Transfer type)
 * - Read-only amount field for Transfer transactions
 * - Validation error display integration
 *
 * Key features:
 * - Expense transactions show only categories with allowExpense=true (excluding user categories)
 * - Transfer transactions show only categories with allowTransfer=true
 * - Income and Adjustment show all active categories
 * - Grandfathered categories persist in dropdowns throughout edit session
 * - Auto-formats amount inputs to 2 decimal places
 *
 * @module components/TransactionList/EditCategoryList
 */
import MoneyInput from '../common/MoneyInput';
import styles from './EditCategoryList.module.css';

/**
 * EditCategoryList Component
 *
 * Handles rendering the category list for edit mode with transaction-type-specific
 * category filtering and grandfathering support.
 *
 * @param {string} transactionType - Type of transaction (Expense, Income, Transfer, Adjustment)
 * @param {string} amount - Total transaction amount
 * @param {Array<Object>} categories - Array of category objects with {category, amount}
 * @param {Object} errors - Validation errors object
 * @param {Function} onCategoryUpdate - Handler for category/amount updates
 * @param {Function} onCategoryAdd - Handler for adding new category row
 * @param {Function} onCategoryRemove - Handler for removing category row
 * @param {Function} getAvailableCategories - Get all available categories for dropdown
 * @param {Function} getAvailableCategoriesForExpenses - Get expense-allowed categories
 * @param {Function} getAvailableCategoriesForTransfers - Get transfer-allowed categories
 * @param {Function} isGrandfatheredCategory - Check if category is grandfathered
 * @param {Function} clearCategoryError - Clear validation error for specific field
 */
function EditCategoryList({
    transactionType,
    amount,
    categories,
    errors,
    onCategoryUpdate,
    onCategoryAdd,
    onCategoryRemove,
    getAvailableCategories,
    getAvailableCategoriesForExpenses,
    getAvailableCategoriesForTransfers,
    isGrandfatheredCategory,
    clearCategoryError
}) {
    const getAvailableCategoriesForRow = (index) => {
        if (transactionType === 'Expense') {
            return getAvailableCategoriesForExpenses(index, true);
        } else if (transactionType === 'Transfer') {
            return getAvailableCategoriesForTransfers(index, true);
        }
        return getAvailableCategories(index, true);
    };

    const hasCategoryError = (index) => {
        return errors.categories && errors.categories[index] && errors.categories[index].category;
    };

    const hasAmountError = (index) => {
        return errors.categories && errors.categories[index] && errors.categories[index].amount;
    };

    const handleAmountChange = (index, parsedValue) => {
        clearCategoryError(index, 'amount');
        const value = parsedValue !== null ? parsedValue.toString() : '';
        onCategoryUpdate(index, 'amount', value);
    };

    const renderAmountInput = (catItem, index) => {
        if (transactionType === 'Transfer') {
            return (
                <MoneyInput
                    value={amount ? parseFloat(amount) : null}
                    onChange={() => {}}
                    placeholder="Amount"
                    disabled={true}
                    className={`${styles.amountInput} ${styles.readOnly} ${hasAmountError(index) ? styles.error : ''}`}
                />
            );
        }

        return (
            <MoneyInput
                value={catItem.amount ? parseFloat(catItem.amount) : null}
                onChange={(parsedValue) => handleAmountChange(index, parsedValue)}
                placeholder="Amount"
                min={0}
                max={9999999.99}
                className={`${styles.amountInput} ${hasAmountError(index) ? styles.error : ''}`}
            />
        );
    };

    const showRemoveButton = () => {
        return categories.length > 1 && transactionType !== 'Transfer';
    };

    const showAddButton = (index) => {
        const isLastRow = index === categories.length - 1;
        const hasAvailable = getAvailableCategoriesForRow(index).length > 0;
        const notTransfer = transactionType !== 'Transfer';
        return isLastRow && hasAvailable && notTransfer;
    };

    return (
        <div className={styles.container}>
            <h5 className={styles.heading}>Categories:</h5>
            {categories.map((catItem, idx) => (
                <div key={idx} className={styles.categoryRow}>
                    <select
                        value={catItem.category}
                        onChange={(e) => {
                            onCategoryUpdate(idx, 'category', e.target.value);
                            clearCategoryError(idx, 'category');
                        }}
                        className={`${styles.categorySelect} ${hasCategoryError(idx) ? styles.error : ''}`}
                    >
                        <option value="">Select Category</option>
                        {getAvailableCategoriesForRow(idx).map(cat => {
                            const isGrandfathered = isGrandfatheredCategory && isGrandfatheredCategory(cat, true);
                            return (
                                <option
                                    key={cat}
                                    value={cat}
                                    className={isGrandfathered ? styles.grandfatheredOption : ''}
                                >
                                    {cat}
                                </option>
                            );
                        })}
                        {catItem.category && !getAvailableCategoriesForRow(idx).includes(catItem.category) && (
                            <option
                                key={catItem.category}
                                value={catItem.category}
                                className={isGrandfatheredCategory && isGrandfatheredCategory(catItem.category, true) ? styles.grandfatheredOption : ''}
                            >
                                {catItem.category}
                            </option>
                        )}
                    </select>

                    {renderAmountInput(catItem, idx)}

                    {showRemoveButton() && (
                        <button
                            onClick={() => onCategoryRemove(idx)}
                            className={styles.removeButton}
                        >
                            Remove
                        </button>
                    )}

                    {showAddButton(idx) && (
                        <button
                            onClick={onCategoryAdd}
                            className={styles.addButton}
                        >
                            Add
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default EditCategoryList;
